import { competenciaParaInputMes } from "../../utils/FormatarCompetencia";

// Competências usam o formato MM/yyyy, o mesmo da API. Toda conta de meses é
// feita por aritmética de índices (ano * 12 + mês), sem Date/UTC, para que a
// virada de dezembro para janeiro não dependa de fuso horário.

export const NOMES_DOS_MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

export const ATALHOS_DE_PERIODO = {
  MES_ATUAL: "MES_ATUAL",
  ULTIMOS_3_MESES: "ULTIMOS_3_MESES",
  ULTIMOS_6_MESES: "ULTIMOS_6_MESES",
  ANO_ATUAL: "ANO_ATUAL",
};

const doisDigitos = (valor) => String(valor).padStart(2, "0");

export const competenciaParaIndice = (competencia) => {
  const inputMes = competenciaParaInputMes(competencia);
  if (!inputMes) {
    return null;
  }

  const [ano, mes] = inputMes.split("-").map(Number);
  return ano * 12 + (mes - 1);
};

export const indiceParaCompetencia = (indice) => {
  const ano = Math.floor(indice / 12);
  const mes = (indice % 12) + 1;

  return `${doisDigitos(mes)}/${ano}`;
};

export const montarCompetencia = (ano, mes) => {
  return `${doisDigitos(mes)}/${ano}`;
};

export const getCompetenciaAtualLocal = (agora = new Date()) => {
  return montarCompetencia(agora.getFullYear(), agora.getMonth() + 1);
};

export const deslocarCompetencia = (competencia, meses) => {
  const indice = competenciaParaIndice(competencia);
  if (indice === null) {
    return "";
  }

  return indiceParaCompetencia(indice + meses);
};

export const separarCompetencia = (competencia) => {
  const inputMes = competenciaParaInputMes(competencia);
  if (!inputMes) {
    return { ano: "", mes: "" };
  }

  const [ano, mes] = inputMes.split("-");
  return { ano: Number(ano), mes: Number(mes) };
};

export const calcularPeriodoPorAtalho = (atalho, agora = new Date()) => {
  const atual = getCompetenciaAtualLocal(agora);
  const ano = agora.getFullYear();

  switch (atalho) {
    case ATALHOS_DE_PERIODO.ULTIMOS_3_MESES:
      return { inicio: deslocarCompetencia(atual, -2), fim: atual };
    case ATALHOS_DE_PERIODO.ULTIMOS_6_MESES:
      return { inicio: deslocarCompetencia(atual, -5), fim: atual };
    case ATALHOS_DE_PERIODO.ANO_ATUAL:
      return {
        inicio: montarCompetencia(ano, 1),
        fim: montarCompetencia(ano, 12),
      };
    case ATALHOS_DE_PERIODO.MES_ATUAL:
    default:
      return { inicio: atual, fim: atual };
  }
};

export const periodoEhValido = (inicio, fim) => {
  const indiceInicio = competenciaParaIndice(inicio);
  const indiceFim = competenciaParaIndice(fim);

  if (indiceInicio === null || indiceFim === null) {
    return false;
  }

  return indiceInicio <= indiceFim;
};

export const periodoTemUmSoMes = (inicio, fim) => {
  const indiceInicio = competenciaParaIndice(inicio);
  const indiceFim = competenciaParaIndice(fim);

  return indiceInicio !== null && indiceInicio === indiceFim;
};

export const formatarCompetenciaPorExtenso = (competencia) => {
  const { ano, mes } = separarCompetencia(competencia);
  if (!ano || !mes) {
    return "-";
  }

  return `${NOMES_DOS_MESES[mes - 1]} de ${ano}`;
};

export const formatarPeriodo = (inicio, fim) => {
  if (periodoTemUmSoMes(inicio, fim)) {
    return formatarCompetenciaPorExtenso(inicio);
  }

  return `${formatarCompetenciaPorExtenso(inicio)} a ${formatarCompetenciaPorExtenso(fim)}`;
};

// A API devolve as competências do mês mais recente para o mais antigo.
// Tabela e gráfico precisam da ordem cronológica.
export const ordenarLinhasPorCompetencia = (linhas) => {
  if (!Array.isArray(linhas)) {
    return [];
  }

  return [...linhas].sort((a, b) => {
    const indiceA = competenciaParaIndice(a?.competencia) ?? 0;
    const indiceB = competenciaParaIndice(b?.competencia) ?? 0;
    return indiceA - indiceB;
  });
};

const paraNumero = (valor) => {
  const numero = Number(valor || 0);
  return Number.isNaN(numero) ? 0 : numero;
};

const arredondarMoeda = (valor) => {
  return Math.round(valor * 100) / 100;
};

// Consolida todas as competências do período, sem depender da posição das
// linhas. Sobra = Caixa - Total Despesas; Sobra % = Sobra / Caixa * 100, com
// 0 quando o caixa é zero. O crescimento é o do mês mais recente do período.
export const consolidarLinhas = (linhas) => {
  const linhasOrdenadas = ordenarLinhasPorCompetencia(linhas);
  const maisRecente = linhasOrdenadas[linhasOrdenadas.length - 1];

  const totalDespesas = arredondarMoeda(
    linhasOrdenadas.reduce(
      (soma, item) => soma + paraNumero(item.totalDespesas),
      0,
    ),
  );
  const valorCaixa = arredondarMoeda(
    linhasOrdenadas.reduce(
      (soma, item) => soma + paraNumero(item.valorCaixa),
      0,
    ),
  );
  const sobraValor = arredondarMoeda(valorCaixa - totalDespesas);
  const sobraPercentual =
    valorCaixa === 0 ? 0 : arredondarMoeda((sobraValor / valorCaixa) * 100);

  return {
    totalDespesas,
    valorCaixa,
    sobraValor,
    sobraPercentual,
    crescimentoDespesasValor: paraNumero(maisRecente?.crescimentoDespesasValor),
    crescimentoDespesasPercentual: paraNumero(
      maisRecente?.crescimentoDespesasPercentual,
    ),
  };
};

export const montarDadosDoGrafico = (linhasOrdenadas) => {
  return linhasOrdenadas.map((item) => ({
    mes: item.competencia,
    despesas: paraNumero(item.totalDespesas),
    sobra: paraNumero(item.sobraValor),
    caixa: paraNumero(item.valorCaixa),
  }));
};
